import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Energy & Power — 8 ANIMATED templates
   solar_panel, wind_turbine, battery_cell, nuclear_reactor,
   hydroelectric_dam, power_grid_diagram, flame_fire, lightning_storm
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Solar Panel ──────────────────────────────────────── */
export const SolarPanel: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Solar Energy";
  const fillPercent = (props.fillPercent as number) ?? 50;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="sp-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d47a1" /><stop offset="100%" stopColor="#42a5f5" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#sp-sky)" rx="16" />
      {/* Sun with pulsing rays */}
      <circle cx="650" cy="100" r="50" fill="#fff9c4" opacity="0.9">
        <animate attributeName="r" values="50;55;50" dur="2s" repeatCount="indefinite" />
      </circle>
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
        const rad = a * Math.PI / 180;
        return (
          <line key={a} x1={650 + Math.cos(rad) * 60} y1={100 + Math.sin(rad) * 60}
            x2={650 + Math.cos(rad) * 80} y2={100 + Math.sin(rad) * 80}
            stroke="#fff9c4" strokeWidth="3" strokeLinecap="round">
            <animate attributeName="x2" values={`${650 + Math.cos(rad) * 80};${650 + Math.cos(rad) * 90};${650 + Math.cos(rad) * 80}`} dur="2s" repeatCount="indefinite" />
            <animate attributeName="y2" values={`${100 + Math.sin(rad) * 80};${100 + Math.sin(rad) * 90};${100 + Math.sin(rad) * 80}`} dur="2s" repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Animated sun rays hitting panel */}
      {[0, 1, 2].map((i) => (
        <line key={i} x1={580 - i * 40} y1={130 + i * 20} x2={350 - i * 30} y2={250 + i * 15}
          stroke="#fff9c4" strokeWidth="1.5" strokeDasharray="6,8" opacity="0.3">
          <animate attributeName="stroke-dashoffset" from="14" to="0" dur="1s" repeatCount="indefinite" />
        </line>
      ))}
      {/* Solar panel (tilted) */}
      <g transform="translate(200,270) skewY(-10)">
        {/* Panel frame */}
        <rect x="0" y="0" width="300" height="180" rx="4" fill="#1a237e" stroke="#283593" strokeWidth="3" />
        {/* Grid cells */}
        {Array.from({ length: 4 }, (_, r) =>
          Array.from({ length: 6 }, (_, c) => (
            <rect key={`${r}-${c}`} x={8 + c * 48} y={8 + r * 43} width="44" height="39" rx="2"
              fill={fillPercent > (r * 6 + c) / 24 * 100 ? "#1565c0" : "#0d47a1"}
              stroke="#283593" strokeWidth="1">
              {fillPercent > (r * 6 + c) / 24 * 100 && (
                <animate attributeName="fill" values="#1565c0;#1976d2;#1565c0" dur="3s" repeatCount="indefinite" begin={`${(r * 6 + c) * 0.1}s`} />
              )}
            </rect>
          ))
        )}
      </g>
      {/* Stand */}
      <rect x="340" y="450" width="10" height="80" fill="#546e7a" />
      <rect x="310" y="530" width="70" height="8" rx="4" fill="#546e7a" />
      {/* Energy output indicator */}
      <rect x="600" y="350" width="120" height="30" rx="6" fill="#1c2833" stroke="#4caf50" strokeWidth="1.5" />
      <rect x="604" y="354" width={Math.min(112, fillPercent * 1.12)} height="22" rx="4" fill="#4caf50" opacity="0.7">
        <animate attributeName="opacity" values="0.7;0.9;0.7" dur="1.5s" repeatCount="indefinite" />
      </rect>
      <text x="660" y="370" textAnchor="middle" fill="#ecf0f1" fontSize="11">{Math.round(fillPercent)}% output</text>
      <text x="400" y="575" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Wind Turbine ─────────────────────────────────────── */
export const WindTurbine: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Wind Energy";
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <linearGradient id="wt-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1565c0" /><stop offset="100%" stopColor="#90caf9" />
        </linearGradient>
      </defs>
      <rect width="800" height="600" fill="url(#wt-sky)" rx="16" />
      {/* Wind lines animated */}
      {[150, 220, 300, 380].map((y, i) => (
        <line key={i} x1={50 + i * 20} y1={y} x2={200 + i * 20} y2={y}
          stroke="#ecf0f1" strokeWidth="1.5" opacity="0.2" strokeDasharray="20,10">
          <animate attributeName="x1" from={50 + i * 20} to={250 + i * 20} dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="x2" from={200 + i * 20} to={400 + i * 20} dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
        </line>
      ))}
      {/* Tower */}
      <polygon points="390,200 410,200 420,530 380,530" fill="#bdbdbd" stroke="#9e9e9e" strokeWidth="1" />
      {/* Nacelle */}
      <rect x="380" y="185" width="40" height="25" rx="6" fill="#e0e0e0" stroke="#9e9e9e" strokeWidth="1" />
      {/* Blades rotating */}
      <g transform={`rotate(${rotation}, 400, 197)`}>
        {[0, 120, 240].map((angle) => (
          <g key={angle} transform={`rotate(${angle}, 400, 197)`}>
            <path d="M400,197 L395,60 Q400,50 405,60 L400,197" fill="#ecf0f1" stroke="#bdbdbd" strokeWidth="1" />
          </g>
        ))}
        <circle cx="400" cy="197" r="8" fill="#e0e0e0" stroke="#9e9e9e" strokeWidth="2" />
      </g>
      {/* Ground */}
      <rect x="0" y="530" width="800" height="70" fill="#2e7d32" opacity="0.5" />
      {/* Energy symbols */}
      <text x="400" y="500" textAnchor="middle" fill="#f9a825" fontSize="16">⚡</text>
      <text x="400" y="570" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Battery Cell ─────────────────────────────────────── */
export const BatteryCell: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Battery";
  const fillPercent = (props.fillPercent as number) ?? 75;
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Battery body */}
      <rect x="250" y="100" width="300" height="400" rx="20" fill="#1c2833" stroke="#546e7a" strokeWidth="4" />
      {/* Terminal */}
      <rect x="360" y="80" width="80" height="25" rx="8" fill="#546e7a" />
      {/* Fill level with animated glow */}
      <rect x="270" y={500 - fillPercent * 3.8} width="260" height={fillPercent * 3.8} rx="12"
        fill={fillPercent > 50 ? "#4caf50" : fillPercent > 20 ? "#f9a825" : "#e74c3c"} opacity="0.6">
        <animate attributeName="opacity" values="0.5;0.7;0.5" dur="2s" repeatCount="indefinite" />
      </rect>
      {/* Charge level marks */}
      {[25, 50, 75].map((pct) => (
        <line key={pct} x1="275" y1={500 - pct * 3.8} x2="545" y2={500 - pct * 3.8}
          stroke="#546e7a" strokeWidth="1" strokeDasharray="6,4" />
      ))}
      {/* Percentage text */}
      <text x="400" y="350" textAnchor="middle" fill="#ecf0f1" fontSize="48" fontWeight="700">{Math.round(fillPercent)}%</text>
      {/* Internal parts */}
      {[
        { y: 130, lbl: "+ Cathode", idx: 0 },
        { y: 310, lbl: "Electrolyte", idx: 1 },
        { y: 470, lbl: "- Anode", idx: 2 },
      ].map((p) => (
        <text key={p.idx} x="610" y={p.y} fill={activeIndex === p.idx ? "#f9a825" : "#7f8c8d"} fontSize="13"
          fontWeight={activeIndex === p.idx ? "600" : "400"}>{p.lbl}</text>
      ))}
      {/* Animated electron flow */}
      {fillPercent > 10 && (
        <g>
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx="400" cy="0" r="4" fill="#42a5f5">
              <animate attributeName="cy" from="480" to="120" dur="2s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.8;0.2;0.8" dur="2s" begin={`${i * 0.5}s`} repeatCount="indefinite" />
            </circle>
          ))}
        </g>
      )}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Nuclear Reactor ──────────────────────────────────── */
export const NuclearReactor: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Nuclear Reactor";
  const flowProgress = (props.flowProgress as number) || 0;
  const activeIndex = (props.activeIndex as number) ?? -1;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Containment dome */}
      <path d="M200,400 L200,250 Q200,100 350,100 Q500,100 500,250 L500,400"
        fill={activeIndex === 0 ? "#1c2833" : "#161b22"} stroke="#546e7a" strokeWidth="3" />
      {/* Core glow */}
      <circle cx="350" cy="320" r="60" fill="#4caf50" opacity="0.1">
        <animate attributeName="r" values="55;65;55" dur="1.5s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.1;0.2;0.1" dur="1.5s" repeatCount="indefinite" />
      </circle>
      {/* Fuel rods */}
      {[310, 330, 350, 370, 390].map((x) => (
        <rect key={x} x={x} y="270" width="8" height="100" rx="2" fill="#f9a825" opacity="0.7">
          <animate attributeName="opacity" values="0.5;0.9;0.5" dur="1s" repeatCount="indefinite" />
        </rect>
      ))}
      {/* Steam generator */}
      <rect x="540" y="150" width="80" height="250" rx="12" fill="#1c2833" stroke={activeIndex === 1 ? "#f9a825" : "#546e7a"} strokeWidth="2" />
      {/* Pipe from reactor to steam generator */}
      <path d="M500,280 L540,280" stroke="#e74c3c" strokeWidth="6">
        <animate attributeName="stroke-dashoffset" from="10" to="0" dur="0.5s" repeatCount="indefinite" />
      </path>
      <path d="M500,320 L540,320" stroke="#3498db" strokeWidth="6" strokeDasharray="8,4">
        <animate attributeName="stroke-dashoffset" from="0" to="12" dur="0.5s" repeatCount="indefinite" />
      </path>
      {/* Cooling tower */}
      <path d="M660,420 Q660,200 700,150 Q740,200 740,420" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      {/* Steam from tower */}
      {flowProgress > 0.2 && [0, 1, 2].map((i) => (
        <ellipse key={i} cx={700 + i * 5} cy={120 - i * 25} rx={15 + i * 8} ry={8 + i * 5} fill="#bdbdbd" opacity={0.2 - i * 0.05}>
          <animate attributeName="cy" from={120 - i * 25} to={60 - i * 25} dur={`${3 + i}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values={`${0.2 - i * 0.05};0`} dur={`${3 + i}s`} repeatCount="indefinite" />
        </ellipse>
      ))}
      {/* Turbine */}
      <circle cx="580" cy="440" r="30" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      <line x1="580" y1="410" x2="580" y2="470" stroke="#bdbdbd" strokeWidth="2">
        <animateTransform attributeName="transform" type="rotate" from="0 580 440" to="360 580 440" dur="1s" repeatCount="indefinite" />
      </line>
      {/* Ground */}
      <line x1="100" y1="420" x2="750" y2="420" stroke="#546e7a" strokeWidth="2" />
      {/* Labels */}
      <text x="350" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="11">Reactor Core</text>
      <text x="580" y="470" textAnchor="middle" fill="#7f8c8d" fontSize="10">Turbine</text>
      <text x="700" y="440" textAnchor="middle" fill="#7f8c8d" fontSize="10">Cooling</text>
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 5. Hydroelectric Dam ────────────────────────────────── */
export const HydroelectricDam: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Hydroelectric Dam";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Reservoir water */}
      <rect x="50" y="180" width="300" height="280" fill="#1565c0" opacity="0.4" rx="0" />
      {/* Water surface animation */}
      <path d="M50,180 Q120,170 200,180 Q280,190 350,180" fill="none" stroke="#42a5f5" strokeWidth="2" opacity="0.3">
        <animate attributeName="d" values="M50,180 Q120,170 200,180 Q280,190 350,180;M50,180 Q120,190 200,180 Q280,170 350,180;M50,180 Q120,170 200,180 Q280,190 350,180" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Dam wall */}
      <polygon points="350,120 400,120 420,460 370,460" fill="#546e7a" stroke="#455a64" strokeWidth="2" />
      {/* Water flow through dam */}
      <path d="M400,350 Q450,350 500,380 Q550,410 600,410" fill="none" stroke="#1565c0" strokeWidth="8" strokeLinecap="round">
        <animate attributeName="stroke-dashoffset" from="20" to="0" dur="0.5s" repeatCount="indefinite" />
      </path>
      {/* Turbine inside dam */}
      <circle cx="395" cy="350" r="20" fill="#1c2833" stroke="#f9a825" strokeWidth="2">
        <animateTransform attributeName="transform" type="rotate" from="0 395 350" to="360 395 350" dur={`${1 / (0.1 + flowProgress)}s`} repeatCount="indefinite" />
      </circle>
      {/* Turbine blades */}
      {[0, 60, 120, 180, 240, 300].map((a) => {
        const rad = a * Math.PI / 180;
        return <line key={a} x1="395" y1="350" x2={395 + Math.cos(rad) * 18} y2={350 + Math.sin(rad) * 18} stroke="#f9a825" strokeWidth="2" />;
      })}
      {/* Downstream */}
      <rect x="450" y="400" width="300" height="60" fill="#1565c0" opacity="0.25" rx="0" />
      {/* Power lines */}
      <line x1="420" y1="200" x2="700" y2="200" stroke="#bdbdbd" strokeWidth="2" />
      {[500, 600, 700].map((x) => (
        <g key={x}>
          <rect x={x - 3} y="200" width="6" height="260" fill="#546e7a" />
          <rect x={x - 15} y="195" width="30" height="3" fill="#bdbdbd" />
        </g>
      ))}
      {/* Animated electricity sparks on power lines */}
      {flowProgress > 0.3 && (
        <circle cx="0" cy="200" r="3" fill="#f9a825">
          <animate attributeName="cx" from="420" to="700" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.3;1" dur="0.3s" repeatCount="indefinite" />
        </circle>
      )}
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Power Grid ───────────────────────────────────────── */
export const PowerGridDiagram: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Power Grid";
  const flowProgress = (props.flowProgress as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Power plant */}
      <rect x="60" y="250" width="80" height="100" rx="4" fill="#546e7a" stroke="#455a64" strokeWidth="2" />
      <rect x="75" y="220" width="15" height="30" fill="#455a64" />
      <ellipse cx="82" cy="210" rx="20" ry="10" fill="#757575" opacity="0.4">
        <animate attributeName="ry" values="10;14;10" dur="2s" repeatCount="indefinite" />
      </ellipse>
      <text x="100" y="380" textAnchor="middle" fill="#7f8c8d" fontSize="10">Power Plant</text>
      {/* Transmission tower */}
      {[280, 450].map((x) => (
        <g key={x}>
          <polygon points={`${x - 8},200 ${x + 8},200 ${x + 15},350 ${x - 15},350`} fill="#546e7a" />
          <line x1={x - 25} y1="210" x2={x + 25} y2="210" stroke="#bdbdbd" strokeWidth="2" />
          <line x1={x - 20} y1="230" x2={x + 20} y2="230" stroke="#bdbdbd" strokeWidth="2" />
        </g>
      ))}
      {/* Power lines */}
      <path d="M140,260 Q210,230 280,210" fill="none" stroke="#bdbdbd" strokeWidth="1.5" />
      <path d="M280,210 Q365,190 450,210" fill="none" stroke="#bdbdbd" strokeWidth="1.5" />
      <path d="M450,210 Q550,230 620,280" fill="none" stroke="#bdbdbd" strokeWidth="1.5" />
      {/* Animated electricity pulses */}
      <circle r="4" fill="#f9a825" opacity="0.8">
        <animateMotion dur="3s" repeatCount="indefinite"
          path="M140,260 Q210,230 280,210 Q365,190 450,210 Q550,230 620,280" />
        <animate attributeName="opacity" values="0.9;0.3;0.9" dur="0.5s" repeatCount="indefinite" />
      </circle>
      {/* Transformer */}
      <rect x="600" y="260" width="60" height="60" rx="8" fill="#1c2833" stroke="#f9a825" strokeWidth="2" />
      <text x="630" y="295" textAnchor="middle" fill="#f9a825" fontSize="20">⚡</text>
      <text x="630" y="340" textAnchor="middle" fill="#7f8c8d" fontSize="10">Transformer</text>
      {/* Houses */}
      {[660, 710, 730].map((x, i) => (
        <g key={i}>
          <rect x={x} y={400 + i * 10} width="30" height="25" fill="#1c2833" stroke="#546e7a" strokeWidth="1" />
          <polygon points={`${x - 3},${400 + i * 10} ${x + 15},${385 + i * 10} ${x + 33},${400 + i * 10}`} fill="#546e7a" />
          {/* Window glow */}
          <rect x={x + 10} y={408 + i * 10} width="10" height="10" fill={flowProgress > 0.5 ? "#f9a825" : "#2c3e50"}>
            {flowProgress > 0.5 && <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite" begin={`${i * 0.3}s`} />}
          </rect>
        </g>
      ))}
      {/* Distribution lines */}
      <path d="M660,290 L660,400" stroke="#bdbdbd" strokeWidth="1" />
      <path d="M660,400 L710,410 L730,420" stroke="#bdbdbd" strokeWidth="1" />
      <text x="400" y="530" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Flame & Fire ─────────────────────────────────────── */
export const FlameFire: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Combustion";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const parts = (props.parts as string[]) || ["Fuel", "Heat", "Oxygen"];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Flame layers — animated */}
      {/* Outer flame */}
      <path d="M400,420 Q350,300 380,200 Q400,120 420,200 Q450,300 400,420"
        fill="#ff5722" opacity="0.4">
        <animate attributeName="d" values="M400,420 Q350,300 380,200 Q400,120 420,200 Q450,300 400,420;M400,420 Q340,310 375,190 Q400,110 425,190 Q460,310 400,420;M400,420 Q350,300 380,200 Q400,120 420,200 Q450,300 400,420" dur="0.8s" repeatCount="indefinite" />
      </path>
      {/* Middle flame */}
      <path d="M400,420 Q365,320 385,230 Q400,170 415,230 Q435,320 400,420"
        fill="#ff9800" opacity="0.6">
        <animate attributeName="d" values="M400,420 Q365,320 385,230 Q400,170 415,230 Q435,320 400,420;M400,420 Q355,310 380,220 Q400,160 420,220 Q445,310 400,420;M400,420 Q365,320 385,230 Q400,170 415,230 Q435,320 400,420" dur="0.6s" repeatCount="indefinite" />
      </path>
      {/* Inner flame */}
      <path d="M400,420 Q380,360 390,300 Q400,260 410,300 Q420,360 400,420"
        fill="#fff9c4" opacity="0.7">
        <animate attributeName="d" values="M400,420 Q380,360 390,300 Q400,260 410,300 Q420,360 400,420;M400,420 Q375,350 388,290 Q400,250 412,290 Q425,350 400,420;M400,420 Q380,360 390,300 Q400,260 410,300 Q420,360 400,420" dur="0.4s" repeatCount="indefinite" />
      </path>
      {/* Fire triangle */}
      <polygon points="400,80 240,460 560,460" fill="none" stroke="#546e7a" strokeWidth="2" strokeDasharray="8,6" />
      {/* Triangle vertices = fire triangle */}
      {[
        { x: 400, y: 65, c: "#e74c3c", i: 0 },
        { x: 225, y: 475, c: "#ff9800", i: 1 },
        { x: 575, y: 475, c: "#42a5f5", i: 2 },
      ].map((v) => (
        <g key={v.i}>
          <circle cx={v.x} cy={v.y} r={v.i === activeIndex ? 28 : 24} fill={v.c} opacity={v.i === activeIndex ? 0.9 : 0.4}>
            {v.i === activeIndex && <animate attributeName="r" values="28;32;28" dur="1s" repeatCount="indefinite" />}
          </circle>
          <text x={v.x} y={v.y + 5} textAnchor="middle" fill="#ecf0f1" fontSize="12" fontWeight="600">{parts[v.i]}</text>
        </g>
      ))}
      {/* Sparks */}
      {[0, 1, 2, 3, 4].map((i) => (
        <circle key={i} cx={385 + i * 8} cy="200" r="2" fill="#fff9c4">
          <animate attributeName="cy" from="200" to={100 - i * 15} dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0" dur={`${1 + i * 0.3}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <text x="400" y="550" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Lightning Storm ──────────────────────────────────── */
export const LightningStorm: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Lightning & Storms";
  const phase = (props.phase as number) || 0;
  const flashOn = Math.sin(phase * 5) > 0.9;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill={flashOn ? "#1a237e" : "#0d1117"} rx="16">
        <animate attributeName="fill" values="#0d1117;#1a237e;#0d1117" dur="4s" repeatCount="indefinite" />
      </rect>
      {/* Storm clouds */}
      <g>
        <ellipse cx="300" cy="120" rx="150" ry="60" fill="#37474f">
          <animate attributeName="cx" values="300;310;300" dur="5s" repeatCount="indefinite" />
        </ellipse>
        <ellipse cx="420" cy="100" rx="120" ry="50" fill="#455a64" />
        <ellipse cx="200" cy="110" rx="100" ry="45" fill="#455a64" />
        <ellipse cx="500" cy="130" rx="130" ry="55" fill="#37474f" />
      </g>
      {/* Lightning bolts animated */}
      <polygon points="350,170 330,270 345,270 310,400 380,260 360,260 390,170" fill="#fff9c4" opacity={flashOn ? 0.9 : 0.1}>
        <animate attributeName="opacity" values="0;0;0.9;0.9;0;0;0;0" dur="3s" repeatCount="indefinite" />
      </polygon>
      <polygon points="480,150 465,230 475,230 455,320 500,220 490,220 510,150" fill="#fff9c4" opacity="0">
        <animate attributeName="opacity" values="0;0;0;0;0;0.8;0.8;0" dur="4s" repeatCount="indefinite" />
      </polygon>
      {/* Rain */}
      {Array.from({ length: 30 }, (_, i) => {
        const x = 80 + (i * 23) % 640;
        const delay = (i * 0.15) % 1.5;
        return (
          <line key={i} x1={x} y1={180 + (i % 5) * 20} x2={x - 5} y2={200 + (i % 5) * 20}
            stroke="#42a5f5" strokeWidth="1.5" strokeLinecap="round" opacity="0.3">
            <animate attributeName="y1" from={180 + (i % 5) * 20} to={580} dur="1s" begin={`${delay}s`} repeatCount="indefinite" />
            <animate attributeName="y2" from={200 + (i % 5) * 20} to={600} dur="1s" begin={`${delay}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Ground */}
      <rect x="0" y="500" width="800" height="100" fill="#1b5e20" opacity="0.3" />
      <text x="400" y="560" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
